import React, { useMemo, useState, forwardRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import Layout from '../../../components/layout/AppLayout';
import ConsumptionList from './components/ConsumptionList';
import ConsumptionInsight from './components/ConsumptionInsight';
import { useSidebar } from '../../../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../../../contexts/ProjectsContext';
import Loader from '../../../../shared/components/ui/Loader';
import { ChevronDown, Calendar, Check } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getConsumptionReport } from '../../../services/api/implementation/impl';
import { getSavedInventoryProjectId, saveInventoryProjectId } from '../../../hooks/useInventorySelection';

const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
  <button
    className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-gray-300 transition-colors text-gray-700 min-w-[200px] justify-between"
    onClick={onClick}
    ref={ref}
    type="button">
    <span className="flex items-center gap-2">
      <Calendar className="w-4 h-4 text-gray-500" />
      <span className="font-medium text-sm">{value}</span>
    </span>
    <ChevronDown className="w-4 h-4 text-gray-500 ml-2" />
  </button>
));

CustomDateInput.displayName = 'CustomDateInput';

const InventoryReport = () => {
  const { collapsed } = useSidebar();
  const { projects, projectsForDropdown } = useProjectsList();
  const [projectId, setProjectId] = useState(
    Number(getSavedInventoryProjectId() || projects?.[0]?.id || 0) || ''
  );
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedStage, setSelectedStage] = useState('All Stages');
  const [selectedBlock, setSelectedBlock] = useState('All Blocks');
  const [isProjectOpen, setIsProjectOpen] = useState(false);
  const [isStageOpen, setIsStageOpen] = useState(false);
  const [isBlockOpen, setIsBlockOpen] = useState(false);

  React.useEffect(() => {
    if (!projectId && projects?.[0]?.id) {
      setProjectId(getSavedInventoryProjectId(projects) || projects[0].id);
    }
  }, [projectId, projects]);

  React.useEffect(() => {
    if (projectId) saveInventoryProjectId(projectId);
  }, [projectId]);

  const asOfDate = moment(selectedDate).format('YYYY-MM-DD');
  const { data, isFetching, isError, error } = useQuery({
    queryKey: ['inventory-consumption-report', projectId, asOfDate],
    queryFn: () => getConsumptionReport(projectId, asOfDate),
    enabled: Boolean(projectId)
  });

  const stages = useMemo(() => {
    const names = Array.from(new Set((data?.tasks || []).map((task) => task.stage).filter(Boolean)));
    return ['All Stages', ...names];
  }, [data]);

  const blocks = useMemo(() => {
    const names = Array.from(new Set((data?.tasks || []).map((task) => task.tower).filter(Boolean)));
    return ['All Blocks', ...names];
  }, [data]);

  const tasks = useMemo(() => {
    return (data?.tasks || []).filter((task) => {
      if (selectedStage !== 'All Stages' && task.stage !== selectedStage) return false;
      if (selectedBlock !== 'All Blocks' && task.tower !== selectedBlock) return false;
      return true;
    });
  }, [data, selectedStage, selectedBlock]);

  const selectedProjectName =
    (projectsForDropdown || projects || []).find((project) => Number(project.id) === Number(projectId))
      ?.label ||
    (projectsForDropdown || projects || []).find((project) => Number(project.id) === Number(projectId))
      ?.name ||
    'Select project';

  return (
    <Layout>
      <div className={`bg-[#F8FAFC] min-h-screen pb-10 ${collapsed ? '' : ''}`}>
        <div className="flex flex-col sticky top-0 z-40 bg-[#EEF3F7] backdrop-blur pt-6 px-6 py-2 gap-4">
          <h1 className="text-2xl font-bold text-gray-900">Material Consumption vs Task Plan</h1>

          <div className="flex flex-wrap items-center gap-4">
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setIsProjectOpen(!isProjectOpen);
                  setIsStageOpen(false);
                  setIsBlockOpen(false);
                }}
                className="flex items-center justify-between gap-8 px-4 py-2.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-gray-300 transition-colors min-w-[240px]">
                <span className="font-semibold text-gray-800">{selectedProjectName}</span>
                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isProjectOpen ? 'rotate-180' : ''}`} />
              </button>
              {isProjectOpen && (
                <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-100 rounded-lg shadow-xl py-1 overflow-hidden z-50 max-h-72 overflow-y-auto">
                  {(projectsForDropdown || projects || []).map((option) => (
                    <button
                      type="button"
                      key={option.id}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm font-medium text-gray-700 flex justify-between items-center"
                      onClick={() => {
                        setProjectId(option.id);
                        setIsProjectOpen(false);
                      }}>
                      {option.label || option.name}
                      {Number(projectId) === Number(option.id) && <Check size={16} className="text-green-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative z-40">
              <DatePicker
                selected={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                customInput={<CustomDateInput />}
                dateFormat="dd MMM yyyy"
                maxDate={new Date()}
                popperPlacement="bottom-start"
              />
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setIsStageOpen(!isStageOpen);
                  setIsProjectOpen(false);
                  setIsBlockOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-gray-300 transition-colors text-gray-700 min-w-[140px] justify-between">
                <span className="font-medium text-sm">{selectedStage}</span>
                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isStageOpen ? 'rotate-180' : ''}`} />
              </button>
              {isStageOpen && (
                <div className="absolute top-full left-0 mt-2 w-full min-w-[180px] bg-white border border-gray-100 rounded-lg shadow-xl py-1 overflow-hidden z-50">
                  {stages.map((option) => (
                    <button
                      type="button"
                      key={option}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm font-medium text-gray-700 flex justify-between items-center"
                      onClick={() => {
                        setSelectedStage(option);
                        setIsStageOpen(false);
                      }}>
                      {option}
                      {selectedStage === option && <Check size={16} className="text-green-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setIsBlockOpen(!isBlockOpen);
                  setIsProjectOpen(false);
                  setIsStageOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-gray-300 transition-colors text-gray-700 min-w-[140px] justify-between">
                <span className="font-medium text-sm">{selectedBlock}</span>
                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isBlockOpen ? 'rotate-180' : ''}`} />
              </button>
              {isBlockOpen && (
                <div className="absolute top-full left-0 mt-2 w-full min-w-[180px] bg-white border border-gray-100 rounded-lg shadow-xl py-1 overflow-hidden z-50">
                  {blocks.map((option) => (
                    <button
                      type="button"
                      key={option}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm font-medium text-gray-700 flex justify-between items-center"
                      onClick={() => {
                        setSelectedBlock(option);
                        setIsBlockOpen(false);
                      }}>
                      {option}
                      {selectedBlock === option && <Check size={16} className="text-green-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex-1 text-right">
              <span className="px-3 py-1 bg-green-50 text-green-600 text-xs font-medium rounded-full border border-green-200">
                {data?.attentionCount || 0} items need attention
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6 pt-6 px-6 w-full">
          {isFetching ? (
            <Loader />
          ) : isError ? (
            <p className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
              Could not load consumption report{error?.response?.data?.message ? `: ${error.response.data.message}` : ''}.
            </p>
          ) : (
            <>
              <ConsumptionList tasks={tasks} />
              <ConsumptionInsight
                insights={data?.insights || []}
                recommendation={data?.recommendation || ''}
              />
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default InventoryReport;
